/**
 * Pure geometry for RevealCharsLayer — see docs/reveal-input-characters.md.
 *
 * Kept apart from the DOM plumbing so the error-prone half (cell counting,
 * band spans, text origin) is unit-testable under vitest's node environment.
 *
 * Everything here assumes the field is rendered in one of the reveal fonts:
 * monospaced, letter-spacing zeroed. That is what makes a character's position
 * plain arithmetic — cellIndex * advance — instead of a text-measurement problem.
 */

export type RevealMarkKind = "band" | "tick" | "tab";

export interface MarkSpec {
  kind: RevealMarkKind;
  /** Offset from the text origin, in px. */
  left: number;
  /** Band or tab width in px; 0 for a tick, which is a fixed-width rule. */
  width: number;
}

export interface MarksResult {
  marks: MarkSpec[];
  /** Cells the value occupies — zero-width characters excluded. */
  cells: number;
}

/**
 * Characters that occupy no cell however the font's cmap maps them: the browser
 * discards them before font lookup. Measured in Chromium — each lays out at
 * exactly 0px. They are the whole reason this layer runs in All mode too.
 */
export const ZERO_WIDTH_RE = /[­​‌‍⁠﻿]/;

/**
 * What counts as an "edge" run. Tab and NBSP already carry a glyph in the font;
 * including them here is deliberate — the band's claim is "there is junk at the
 * end of this value", not "this particular character is a space".
 */
export const EDGE_WHITESPACE_RE = /[ \t ]/;

/**
 * Tab. The reveal fonts map it to `→`, but Chromium 153 never reaches the font:
 * a tab lays out as a blank tab stop, whatever the cmap says (measured in a real
 * <input>, 2026-10). So the layer draws the arrow over the tab's cell, and
 * reveal.css pins `tab-size: 1` so that cell is exactly one advance wide —
 * at the inherited `tab-size: 4` a tab spans up to four cells and every mark
 * after it lands short.
 */
export const TAB_RE = /\t/;

export function computeMarks(value: string, cell: number, mode: "edges" | "all"): MarksResult {
  // Code points rather than UTF-16 units, so an astral character counts once.
  const chars = Array.from(value);
  const marks: MarkSpec[] = [];
  let cells = 0;

  for (const ch of chars) {
    if (ZERO_WIDTH_RE.test(ch)) {
      // Sits on the boundary before the next visible cell, since it has no cell
      // of its own to occupy.
      marks.push({ kind: "tick", left: cells * cell, width: 0 });
      continue;
    }
    if (TAB_RE.test(ch)) marks.push({ kind: "tab", left: cells * cell, width: cell });
    cells++;
  }

  if (mode === "edges") {
    let lead = 0;
    while (lead < chars.length && EDGE_WHITESPACE_RE.test(chars[lead] ?? "")) lead++;
    if (lead > 0) marks.push({ kind: "band", left: 0, width: lead * cell });

    // Stop at `lead` so an all-whitespace value yields one band, not two
    // overlapping ones.
    let trail = 0;
    for (let i = chars.length - 1; i >= lead && EDGE_WHITESPACE_RE.test(chars[i] ?? ""); i--) trail++;
    if (trail > 0) marks.push({ kind: "band", left: (cells - trail) * cell, width: trail * cell });
  }

  return { marks, cells };
}

export interface BoxMetrics {
  borderLeft: number;
  borderRight: number;
  borderTop: number;
  borderBottom: number;
  paddingLeft: number;
  paddingRight: number;
  textAlign: string;
}

export interface FieldRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Geometry {
  /** Padding box, in viewport coordinates — the clip window for the marks. */
  clip: FieldRect;
  /** Horizontal translation applied to the mark track. */
  offsetX: number;
}

/**
 * Where the marks go. The clip is the field's padding box (border excluded) so
 * marks are hidden once the field scrolls them out of view; the track offset is
 * the text origin, which is padding plus whatever the alignment adds, minus how
 * far the field has scrolled.
 */
export function computeGeometry(
  rect: FieldRect,
  box: BoxMetrics,
  cells: number,
  cell: number,
  scrollLeft: number,
): Geometry {
  const innerWidth = rect.width - box.borderLeft - box.borderRight;
  const contentWidth = innerWidth - box.paddingLeft - box.paddingRight;
  const textWidth = cells * cell;

  // Alignment only shifts anything while the text still fits; past that the
  // field scrolls and the text is pinned to the start edge.
  let align = 0;
  if (textWidth <= contentWidth) {
    if (box.textAlign === "right" || box.textAlign === "end") align = contentWidth - textWidth;
    else if (box.textAlign === "center") align = (contentWidth - textWidth) / 2;
  }

  return {
    clip: {
      left: rect.left + box.borderLeft,
      top: rect.top + box.borderTop,
      width: innerWidth,
      height: rect.height - box.borderTop - box.borderBottom,
    },
    offsetX: box.paddingLeft + align - scrollLeft,
  };
}


/* ────────────────────────────────────────────────────────────────────────────
 * Multi-line (textarea) regions.
 *
 * Cell arithmetic does not survive soft wrapping: where a long line breaks
 * depends on the browser's line-breaking rules, and being one line out puts
 * every mark below it in the wrong place. So the multi-line path measures
 * instead — a mirror element laid over the field, with Range.getClientRects()
 * for each region. This function only decides WHICH character ranges matter;
 * turning them into rectangles is the DOM layer's job.
 *
 * Offsets are UTF-16 code units, because that is what Range takes. Every
 * character handled here (zero-width, newline, ASCII/NBSP whitespace) is BMP,
 * so a plain index loop is safe.
 * ──────────────────────────────────────────────────────────────────────────── */

export type TextRegionKind = "band" | "tick" | "lineEnd" | "tab";

export interface TextRegion {
  kind: TextRegionKind;
  /** UTF-16 offset into the value. */
  start: number;
  /**
   * Exclusive end. Always a REAL character range, never collapsed — a tick
   * covers its zero-width character and a lineEnd covers its newline. Measured
   * in Chromium: a collapsed Range on an empty line returns a degenerate
   * {0,0,0,0} box, so markers on blank lines landed in the corner. A range over
   * the character itself reports the correct position on every line.
   */
  end: number;
}

export function computeTextRegions(value: string, mode: "edges" | "all"): TextRegion[] {
  const regions: TextRegion[] = [];

  // Zero-width characters and soft hyphens, wherever they are — same reason as
  // the single-line path: the browser never draws them.
  for (let i = 0; i < value.length; i++) {
    if (ZERO_WIDTH_RE.test(value[i] ?? "")) regions.push({ kind: "tick", start: i, end: i + 1 });
    else if (TAB_RE.test(value[i] ?? "")) regions.push({ kind: "tab", start: i, end: i + 1 });
  }

  let lineStart = 0;
  while (lineStart <= value.length) {
    let lineEnd = value.indexOf("\n", lineStart);
    const hardBreak = lineEnd !== -1;
    if (!hardBreak) lineEnd = value.length;

    if (mode === "edges") {
      // Per line, not per value: a trailing space before a newline is exactly
      // the accident this mode exists to catch, and two bands over the whole
      // value could never express it.
      let lead = lineStart;
      while (lead < lineEnd && EDGE_WHITESPACE_RE.test(value[lead] ?? "")) lead++;
      if (lead > lineStart) regions.push({ kind: "band", start: lineStart, end: lead });

      let trail = lineEnd;
      while (trail > lead && EDGE_WHITESPACE_RE.test(value[trail - 1] ?? "")) trail--;
      if (trail < lineEnd) regions.push({ kind: "band", start: trail, end: lineEnd });
    }

    // All mode is "show me everything", so the line breaks themselves are
    // marked. Edges deliberately does not: a newline in a textarea is intended
    // content, and a marker on every line would be pure noise.
    if (hardBreak && mode === "all") {
      regions.push({ kind: "lineEnd", start: lineEnd, end: lineEnd + 1 });
    }

    if (!hardBreak) break;
    lineStart = lineEnd + 1;
  }

  return regions;
}
